import { EstimateActionWorkerMessage } from "@bitwarden/web-vault/app/services/web-worker/estimate-actions/estimate-action.worker.message";
import { WebWorkerImplementation } from "@bitwarden/web-vault/web-worker/web.worker.implementation";

export class EstimateActionWorker extends WebWorkerImplementation<EstimateActionWorkerMessage> {
  _callback: (data: any) => void;
  _workerType = "EstimateActionWorker";
  // Last message sent to the worker — kept so onWorkerError can recover
  // permutationIndex/estimateIndex/estimateActions when the worker crashes
  // before ever posting a "message" back. Without this, ScenarioOption
  // .returnFromEstimateAction would default these to 0/[] and silently mark
  // an incomplete permutation as done instead of correctly advancing it.
  private lastMessage: EstimateActionWorkerMessage;

  protected constructor() {
    // also this can be thread_worker in Node.js environment
    const worker = new Worker(new URL("./estimate-action.job.ts", import.meta.url), {
      type: "module",
    });
    super(worker);

    this.addEventListener("message", this.onMessageFromWorker.bind(this));
  }

  get callback(): (data: any) => void {
    return this._callback;
  }

  set callback(callback: (data: any) => void) {
    this._callback = callback;
  }

  static getInstance(): EstimateActionWorker {
    return new EstimateActionWorker();
  }

  async runEstimateAction(message: EstimateActionWorkerMessage, callback: (data: any) => void) {
    // Post first: if postMessage throws (e.g. DataCloneError) the callback is never attached, so a
    // later "error" event on this reused worker can't fire it a second time. Worker events always
    // arrive asynchronously, so wiring the callback after the post can't miss a reply.
    this.do(message);
    this.callback = callback;
    this.lastMessage = message;
  }

  /**
   * function to handle the message back from the worker service
   * @param message
   */
  onMessageFromWorker(message: MessageEvent): void {
    if (message?.data?.data?.complete && this.callback) {
      // Clear before firing: this worker instance is reused for the next job
      // at this slot, and a stray/delayed "error" or "messageerror" event for
      // *this already-settled* job must find `this.callback` gone instead of
      // re-firing (or misfiring against whatever job reused the slot next).
      const callback = this.callback;
      this.callback = undefined;
      this.lastMessage = undefined;
      callback(message.data);
    }
  }

  protected onWorkerError(event: ErrorEvent | MessageEvent): void {
    // The underlying Worker crashed/failed to load — no "message" is coming.
    // Without this, the callback never fires and the caller hangs forever.
    // workerIndex must be included — WebWorkerQueue.runJob's wrapper reads
    // data?.data?.workerIndex to know which slot to free; omitting it would
    // free the wrong (or no) slot and leak this one as permanently busy.
    // permutationIndex/estimateIndex/estimateActions must also be included —
    // ScenarioOption.returnFromEstimateAction reads these to advance the
    // correct permutation; omitting them defaults to 0/[] and silently marks
    // an incomplete permutation as done, producing a wrong result instead of
    // the hang this handler exists to fix. createdRecords must be included
    // too — otherwise returnFromEstimateAction defaults it to {}, wiping the
    // runningAccountBalances accumulated so far and corrupting the rest of
    // this permutation.
    if (this.callback) {
      // See onMessageFromWorker: clear first so this same event can't also
      // double-fire if the browser dispatches both "error" and
      // "messageerror" for one failure, and so it can't fire again later.
      const callback = this.callback;
      const lastData = this.lastMessage?.data ?? {};
      this.callback = undefined;
      this.lastMessage = undefined;
      callback({
        data: {
          complete: true,
          workerIndex: this.workerIndex,
          error: event instanceof ErrorEvent ? event.message : String(event),
          permutationIndex: lastData.permutationIndex,
          estimateIndex: lastData.estimateIndex,
          estimateActions: lastData.estimateActions,
          createdRecords: lastData.createdRecords,
        },
      });
    }
  }
}
